/* eslint-disable max-len */
import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import Helmet from 'react-helmet';
import styled, {css} from 'styled-components';
import ScrollContainer from 'react-indiana-drag-scroll';

import {capitalize, debugCNitoUrl} from './../helpers';
import {
    notWebMediaQuery,
    mobileMediaQuery,
    isMobile,
    PageBodyWrapper,
    Flex,
    useBreakpoint,
    breakpoints,
} from './../styles';

import {showAccessed} from '../store/actions/recommendation';
import * as ShowActions from '../store/actions/show';

import {contentUrlSolver} from './../helpers';

import Loader from './../components/Loader';
import VideosGrid from '../components/VideosGrid';
import ContentRow from '../components/ContentRow';

const CartoonDetails = props => {
    props = {
        ...props,
        ...useSelector(state => state.episode),
    };

    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams();

    const shows = useSelector(state => state.show);
    const config = useSelector(state => state.config);
    const user = useSelector(state => state.user);
    const idp = user && user.idp;
    const breakpoint = useBreakpoint();

    const {t} = useTranslation();

    const [pageState, setPageState] = useState('loading');
    const [currentShow, setCurrentShow] = useState(undefined);
    const [episodes, setEpisodes] = useState(undefined);

    useEffect(() => {
        if (pageState === 'ready') {
            currentShow && dispatch(showAccessed(currentShow));

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    pageName: `${window.location.hostname}/cartoons/${params.show}`,
                    section: 'Cartoon Detail',
                    subsection: 'nvs',
                    franchise: currentShow.title,
                    contenttype: 'Cartoon Detail',
                    contenttitle: 'Cartoon Detail',
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: `web|${window.location.hostname}`,
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });
        }

        // eslint-disable-next-line
    }, [pageState]);

    useEffect(() => {
        if (shows.show ? !shows.show[params.show] : true) {
            loadShowDetails();
        }

        // eslint-disable-next-line
    }, [user.jwtToken]);

    const loadShowDetails = async () => {
        let response = await dispatch(ShowActions.loadShowDetails(params.show));

        if (response.data?.data?.show) {
            let showData = response.data.data.show;

            showData.metadata = {
                ...showData,
                contentId: showData.id,
                pictureUrl: showData.image,

                metadata: undefined,
                seasons: undefined,
            };

            showData.seasons.map(season => {
                season.contentId = season.id;
                season.metadata = {
                    ...season,
                    objectSubtype: season.type,
                    season: season.number,
                };

                return season;
            });

            setCurrentShow(showData);
        } else {
            history.push(`/error${debugCNitoUrl()}`);
        }
    };

    useEffect(() => {
        if (currentShow) {
            setPageState('ready');

            setEpisodes(
                currentShow.seasons.reduce(
                    (acc, curr) => [...acc, ...curr.vods],
                    [],
                ),
            );
        }
        // eslint-disable-next-line
    }, [currentShow]);

    const handleClickEp = content_id => {
        history.push(`/play/${content_id}${debugCNitoUrl()}`);
    };

    const renderEpisodes = () => {
        if (
            pageState === 'ready' &&
            currentShow &&
            currentShow.seasons.length === 0
        ) {
            return null;
        }
        if (pageState !== 'ready' || !episodes) {
            return <Loader />;
        } else if (pageState === 'ready') {
            if (isMobile) {
                return (
                    <ContentRow
                        width="calc(100% + 50px)"
                        margin="0px -25px"
                        items={episodes}
                        onClickContent={handleClickEp}
                    />
                );
            } else {
                return (
                    <VideosGrid
                        items={episodes}
                        onClickItem={handleClickEp}
                        hideDuration
                        hidePlayButton
                        cnito
                    />
                );
            }
        }
    };

    const renderMobile = () =>
        episodes && (
            <>
                <Flex
                    width="100%"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    wrap="nowrap"
                >
                    <Flex
                        position="relative"
                        direction="column"
                        justifyContent="flex-start"
                        margin="auto 15px auto 40px"
                        height="calc(85vh - 110px)"
                        maxHeight="260px"
                    >
                        <ShowCharacter
                            src={contentUrlSolver(
                                config,
                                currentShow.image,
                                'CHARECTER-MAIN',
                                'png',
                                '?imwidth=1080',
                            )}
                        />

                        <ShowCharacterOverlay
                            background={
                                currentShow &&
                                contentUrlSolver(
                                    config,
                                    currentShow.image,
                                    'BACKGROUND',
                                    'png',
                                    '?imwidth=300',
                                )
                            }
                        />

                        <ShowLogo
                            width="158px"
                            src={contentUrlSolver(
                                config,
                                currentShow.image,
                                'SHOW-LOGO',
                                'png',
                            )}
                            alt="Show logo"
                        />
                    </Flex>

                    <Flex
                        position="relative"
                        direction="column"
                        margin="0 0 0 -12px"
                        overflow="hidden"
                    >
                        <Title>{t('home.newReleases')}</Title>
                        <ScrollFade
                            background={
                                currentShow &&
                                contentUrlSolver(
                                    config,
                                    currentShow.image,
                                    'BACKGROUND',
                                    'png',
                                    '?imwidth=300',
                                )
                            }
                        />
                        <VodsScroll>
                            <VideosGrid
                                items={episodes}
                                onClickItem={handleClickEp}
                                onlyChild
                                hideDuration
                                hidePlayButton
                                cnito
                            />
                        </VodsScroll>
                    </Flex>
                </Flex>
            </>
        );

    const renderDesktop = () => (
        <>
            <ContentWrapper>
                <Flex padding="12px 32px">
                    <img
                        width="318px"
                        src={contentUrlSolver(
                            config,
                            currentShow.image,
                            'CHARECTER-MAIN',
                            'png',
                            '?imwidth=1080',
                        )}
                        alt="Show character"
                    />

                    <img
                        width="158px"
                        src={contentUrlSolver(
                            config,
                            currentShow.image,
                            'SHOW-LOGO',
                            'png',
                        )}
                        alt="Show logo"
                    />
                </Flex>
            </ContentWrapper>

            <VodsOverlay>
                <ContentWrapper flex="1">
                    <Title>{t('home.newReleases')}</Title>

                    {renderEpisodes()}
                </ContentWrapper>
            </VodsOverlay>
        </>
    );

    return (
        <>
            <Helmet>
                <title>Cartoon Network App</title>
            </Helmet>

            <Container
                background={
                    currentShow &&
                    contentUrlSolver(
                        config,
                        currentShow.image,
                        'BACKGROUND',
                        'png',
                        '?imwidth=300',
                    )
                }
            >
                {currentShow === undefined ? (
                    <Loader margin="auto" padding="128px 0px" />
                ) : (
                    <>
                        <Helmet>
                            <title>
                                {`Assista ${capitalize(
                                    currentShow.title,
                                    true,
                                )} | Cartoon Network App`}
                            </title>
                            <meta
                                name="description"
                                content={currentShow.description}
                            ></meta>
                        </Helmet>

                        {['xs', 'mobile', 'sm'].includes(breakpoint)
                            ? renderMobile()
                            : renderDesktop()}
                    </>
                )}
            </Container>
        </>
    );
};

const Container = styled(PageBodyWrapper)`
    margin: 0;
    max-width: initial;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: calc(100vh - 392px);
    width: 100%;
    box-sizing: border-box;
    height: 100%;
    justify-content: flex-start;
    padding-top: 30px;
    padding-bottom: 0px;

    & > #videos-grid {
        margin: 0;
        width: 100%;
    }

    ${notWebMediaQuery} {
        padding-top: 142px;
    }

    ${mobileMediaQuery} {
        max-width: 100vw;
        overflow-x: hidden;
        box-sizing: border-box;
    }

    ${props => css`
        background: url(${props.background});
    `}
`;

const VodsOverlay = styled.div`
    flex: 1;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.15);
    padding-top: 45px;
    padding-bottom: 80px;

    ${notWebMediaQuery} {
        padding-bottom: 32px;
    }
`;

const ContentWrapper = styled(Flex)`
    width: 100%;
    max-width: 1440px;
    margin-left: auto;
    margin-right: auto;
`;

const Title = styled.h2`
    font-size: 20px;
    line-height: 20px;
    text-transform: uppercase;
    text-align: left;
    margin: 0px auto 24px 40px;
    color: #000;
    font-family: 'Rodger';
    font-weight: 900;
    letter-spacing: 2.5px;

    ${mobileMediaQuery} {
        font-size: 18px;
        line-height: 21px;

        margin-bottom: 8px;
    }

    @media screen and (max-width: ${breakpoints.sm}px) {
        margin-left: 12px;
    }
`;

const ShowCharacter = styled.img`
    height: 100%;
    width: auto;
    position: relative;
`;

const ShowCharacterOverlay = styled.div`
    position: absolute;
    height: 30%;
    right: 0px;
    bottom: 0px;
    left: 0px;
    background: url(${props => props.background});
    mask-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 50%,
        rgba(0, 0, 0, 1) 100%
    );
`;

const ShowLogo = styled.img`
    position: absolute;
    width: fill-available;
    height: 30%;
    left: 64px;
    right: 64px;
    bottom: 0px;
    object-fit: contain;
    flex: 1;
`;

const VodsScroll = styled(ScrollContainer)`
    display: grid;
    grid-auto-flow: column;
    grid-column-gap: 14px;
    padding-right: 14px;
    box-sizing: border-box;
    width: 100%;
    padding-bottom: 16px;
    padding-left: 12px;

    & > div {
        width: 265px;
        height: 0px;
        padding-bottom: calc(201 / 265 * 100%);
        max-width: initial;
        max-height: initial;
        min-width: initial;
    }
`;

const ScrollFade = styled.div`
    position: absolute;
    height: 100%;
    bottom: 0px;
    left: 0px;
    top: 0px;
    width: 12px;
    background: url(${props => props.background});
    mask-image: linear-gradient(
        to left,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 100%
    );
    z-index: 1;
`;

export default CartoonDetails;
